import { P2GBox, useTheme } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GText, SvgProtectionNone } from '@parcel2go/core-ui';
import { ReactNode, useMemo } from 'react';
import { QuoteOptionBaseApiModel } from '~/lib/api/smartsend/types';
import { P2GRadio } from '@parcel2go/core-ui';
import { CoverExtra, ExtendedBaseCoverExtra, ExtrasIcon, ExtrasIconSrc } from '../ExtrasHelper';
import { AppCard } from '~/components/card/app-card';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useTaxName } from '@parcel2go/npm-p2g-app';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';

interface props {
  options: QuoteOptionBaseApiModel[];
  selectedKey: string;
  setSelected: (key: string) => void;
  parcelValue?: number;
  currencyCode: string;
}

const ExtraRow: React.FC<{
  extra: string;
  icon?: ReactNode;
  palette: 'primary' | 'error';
  justify?: 'center';
  children: ReactNode;
}> = (props) => {
  return (
    <P2GGrid container gap={2} wrap="nowrap" alignItems="center">
      <P2GGrid item flexShrink={1}>
        <CdnIconProxy size="md" palette={props.palette} src={ExtrasIconSrc({ Id: props.extra }) || ''}>
          {props.icon || <ExtrasIcon Id={props.extra} />}
        </CdnIconProxy>
      </P2GGrid>
      <P2GGrid item flexGrow={1}>
        {props.children}
      </P2GGrid>
    </P2GGrid>
  );
};

const GetLabel: React.FC<{ option: QuoteOptionBaseApiModel; parcelValue: number; currencyCode: string }> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  if (!props.option) {
    return null;
  }
  switch (props.option.CoverPercentage) {
    case 100:
      return (
        <ExtraRow extra={CoverExtra} palette="primary">
          <P2GText>
            <strong>{t('FULL_PARCEL_PROTECTION', { defaultValue: 'Full Parcel Protection' })}</strong>
          </P2GText>
          {props.parcelValue == null ? (
            <P2GText variant="body2" component="p">
              {t('PARCEL_PROTECTION_DESCRIPTION', {
                defaultValue: 'Your parcels will be protected for their full value <br /> (service limits apply).'
              })}
            </P2GText>
          ) : (
            <P2GText variant="body2" component="p">
              <Trans
                i18nKey="PARCEL_PROTECTION_DESCRIPTION_ALTERNATIVE"
                t={t}
                values={{ value: toDisplayCurrency(props.currencyCode, props.option.Covered) }}
                defaults="Your parcel will be protected for the full value of {{value}}."
              />
            </P2GText>
          )}
        </ExtraRow>
      );
    case 0:
      return (
        <ExtraRow extra={ExtendedBaseCoverExtra} icon={<SvgProtectionNone />} palette="error">
          <P2GText colour="error">
            <strong>{t('NO_PARCEL_PROTECTION_ALERT', { defaultValue: 'No Protection at all?' })}</strong>
          </P2GText>
          {props.parcelValue == null ? (
            <P2GText variant="body2" component="p">
              <Trans
                i18nKey="NO_PARCEL_PROTECTION_WARNING"
                t={t}
                defaults="I am willing to risk my parcels. <br /> I'm not worried about potential loss or damage."
              />
            </P2GText>
          ) : (
            <P2GText variant="body2" component="p">
              <Trans
                i18nKey="PARCEL_PROTECTION_WARNING"
                values={{
                  value: toDisplayCurrency(props.currencyCode, props.parcelValue)
                }}
                t={t}
                defaults="I am willing to risk my {{value}} parcel. <br /> I'm not worried about potential loss or damage."
              />
            </P2GText>
          )}
        </ExtraRow>
      );
    default:
      return (
        <ExtraRow extra={ExtendedBaseCoverExtra} palette="primary">
          <P2GText>
            <strong>{t('PARCEL_PROTECTION_ALERT', { defaultValue: 'Parcel Protection' })}</strong>
          </P2GText>
          {props.parcelValue == null ? (
            <P2GText variant="body2" component="p">
              <Trans
                i18nKey="DEFAULT_PROTECTION_DESCRIPTION"
                t={t}
                defaults="Your parcels will include parcel protection <br /> (service limits apply)."
              />
            </P2GText>
          ) : (
            <P2GText variant="body2" component="p">
              <Trans
                i18nKey="DEFAULT_PARCEL_PROTECTION_DESCRIPTION_ALTERNATIVE"
                t={t}
                values={{
                  value: toDisplayCurrency(props.currencyCode, props.option.Covered)
                }}
                defaults="Your parcel will be protected for {{value}}."
              />
            </P2GText>
          )}
        </ExtraRow>
      );
  }
};

export const ProtectionSelectionDisplay: React.FC<props> = (props) => {
  const theme = useTheme();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const taxName = useTaxName();

  const getButtonLabel = (option: QuoteOptionBaseApiModel) => {
    if (!option) {
      return null;
    }
    switch (option.CoverPercentage) {
      case 100:
        return t('FULL_BUTTON_LABEL', { defaultValue: 'Full' });
      case 0:
        return t('NONE_BUTTON_LABEL', { defaultValue: 'None' });
      default:
        return t('BASIC_BUTTON_LABEL', { defaultValue: 'Basic' });
    }
  };

  const selected = useMemo(
    () => props.options.find((x) => x.Key === props.selectedKey) || props.options[0],
    [props.selectedKey, props.options]
  );

  const selectedIndex = props.options.indexOf(selected);

  return (
    <>
      {props.options.length > 1 ? (
        <AppCard>
          <P2GBox p={3}>
            <P2GGrid container spacing={1} direction="row">
              {props.options.map((o, i) => {
                const isSelected = selectedIndex === i;
                const valDif = isSelected ? o.CoverCost : o.CoverCost - (selected?.CoverCost || 0);
                const showPrice = selectedIndex <= i && o.CoverCost > 0;
                return (
                  <P2GGrid item xs={true} key={o.Key}>
                    <P2GBox textAlign="center">
                      <P2GRadio
                        id={o.Key}
                        name="Protection"
                        checked={o.Key === props.selectedKey}
                        value={o.Key}
                        onChange={() => {
                          if (o.Key) {
                            props.setSelected(o.Key);
                          }
                        }}
                        label={
                          <>
                            <P2GText>
                              <strong>{getButtonLabel(o)}</strong>
                            </P2GText>
                            {props.parcelValue != null && showPrice && (
                              <P2GText component="p">
                                <Trans
                                  i18nKey={`PROTECTION_PRICE`}
                                  t={t}
                                  components={{ small: <P2GText small /> }}
                                  values={{
                                    price:
                                      valDif > 0
                                        ? `+ ${toDisplayCurrency(props.currencyCode, valDif)}`
                                        : toDisplayCurrency(props.currencyCode, valDif),
                                    taxName: taxName
                                  }}
                                  defaults={`{{price}} <small>exc {{taxName}}</small>`}
                                />
                              </P2GText>
                            )}
                          </>
                        }
                      />
                    </P2GBox>
                  </P2GGrid>
                );
              })}
            </P2GGrid>
            {selected && (
              <div
                style={{
                  borderRadius: 6,
                  backgroundColor: '#f1f1f1',
                  overflow: 'hidden',
                  marginTop: 10
                }}
              >
                <div
                  style={{
                    backgroundColor: theme.palette.success.main,
                    height: '12px',
                    width: `${selected.CoverPercentage}%`,
                    transition: 'width 0.5s'
                  }}
                ></div>
              </div>
            )}
            {selected && (
              <P2GBox mt={2}>
                <GetLabel option={selected} parcelValue={props.parcelValue || 0} currencyCode={props.currencyCode} />
              </P2GBox>
            )}
          </P2GBox>
        </AppCard>
      ) : (
        <GetLabel option={selected} parcelValue={props.parcelValue || 0} currencyCode={props.currencyCode} />
      )}
    </>
  );
};
